import React, { Component, memo } from 'react';
import PropTypes from 'prop-types';
import cc from 'classcat';
import { connect } from 'react-redux';
import Media from 'components/Media';
import Api from 'services/Api';
import { ContainerQuery } from 'lib/react-container-query';
import Online from 'components/Online';
import OfflineOverlay from 'components/OfflineOverlay';
import LockedOverlay from 'components/LockedOverlay';
import Countdown from 'components/Countdown';
import FlipClock from 'components/FlipClock';
import Carousel from 'components/Carousel';
import QuickBuy from 'components/QuickBuy';
import Bingo from 'modules/Bingo';
import CarouselMod from 'modules/Carousel';
import { transformMiddleQuery, headerQuery } from '../lib/queries';
import TicketList from '../TicketList';
import LiveBalls from '../LiveBalls';
import BingoInfoPanel from './BingoInfoPanel';
import './bingoMain.css';
import './bingoMiddle.css';

const SECOND = 1000;

const View = memo(
  ({
    bottom,
    isActive,
    ticketSelect,
    refName,
    nextAvailableTicketRefs,
    nextAvailableTickets,
    nextSelectedTicketRefs,
    ticketPrice,
    priceLabel,
    nextPurchasedTicketRefs,
    nextPurchasedTickets,
    currentPurchasedTicketRefs,
    currentPurchasedTickets
  }) => {
    if (bottom === 'select-tickets') {
      return (
        <TicketList
          refName={refName}
          isActive={isActive}
          refs={nextAvailableTicketRefs}
          entities={nextAvailableTickets}
          selectedRefs={nextSelectedTicketRefs}
          ticketSelect={ticketSelect}
          ticketPrice={ticketPrice}
          priceLabel={priceLabel}
        />
      );
    } else if (bottom === 'quick-buy' || bottom === 'buy-next' || bottom === 'buy-in-progress') {
      return <QuickBuy refName={refName} />;
    } else if (bottom === 'my-tickets') {
      return (
        <TicketList
          refName={refName}
          refs={nextPurchasedTicketRefs}
          entities={nextPurchasedTickets}
          isActive={isActive}
        />
      );
    } else if (bottom === 'current-game') {
      return (
        <TicketList
          refName={refName}
          refs={currentPurchasedTicketRefs}
          entities={currentPurchasedTickets}
          isActive={isActive}
        />
      );
    } else {
      return null;
    }
  }
);

View.displayName = 'View';

class _BingoMiddle extends Component {
  componentDidMount() {
    const { inPlay, inProgress, showInPlay, showInProgress } = this.props;
    if (inPlay) {
      showInPlay();
    } else if (!inPlay && inProgress) {
      showInProgress();
    }
  }

  componentDidUpdate(prevProps) {
    const { inPlay, inProgress, showInPlay, showInProgress, showWaiting } = this.props;

    if (!prevProps.inPlay && inPlay) {
      showInPlay();
    } else if (!prevProps.inPlay && !prevProps.inProgress && inProgress) {
      showInProgress();
    } else if ((prevProps.inPlay && !inPlay) || (prevProps.inProgress && !inProgress)) {
      showWaiting();
    }
  }

  ticketSelect = (ticketRefs, selected) => {
    if (!selected) {
      if (this.props.nextAvailableTicketRefs.includes(ticketRefs[0])) {
        this.props.selectTicket(ticketRefs);
      }
    } else {
      this.props.deselectTicket(ticketRefs);
    }
  };

  render() {
    const {
      inPlay,
      startDate,
      refresh,
      updateStartDate,
      nextPurchasedTicketRefs,
      refName,
      logoImageUrl,
      locked,
      inProgress
    } = this.props;
    return (
      <div
        className={`bingoMain ${inPlay ? 'playing' : ''} ${
          inProgress && !inPlay ? 'in-progress' : ''
        }`}
      >
        <Online provider="Bingo">
          {(online) =>
            !online ? <OfflineOverlay name="bingo" /> : online && locked ? <LockedOverlay /> : null
          }
        </Online>
        <Media query="(max-width: 1024px)">
          {(matches) => (
            <ContainerQuery query={headerQuery}>
              {(containerQuery) => (
                <div className={cc(['header', containerQuery])}>
                  {matches ? (
                    <BingoInfoPanel refName={this.props.refName} type={this.props.type} />
                  ) : null}
                  {!matches ? (
                    <button className="backButton circularButton button">B</button>
                  ) : null}
                  {!matches ? (
                    <img
                      src={logoImageUrl || '/assets/images1/games/room-logo-default.svg'}
                      className="image headerRoomImage placeholder"
                    />
                  ) : null}
                  {(() => {
                    if (refName) {
                      return inPlay || inProgress ? (
                        <LiveBalls refName={refName} />
                      ) : (
                        <div id="mainClock" className="gameClock loaded">
                          <Countdown
                            expirationDate={startDate}
                            onStart={refresh}
                            onEnd={updateStartDate}
                            updateInterval={SECOND}
                          >
                            <FlipClock />
                          </Countdown>
                        </div>
                      );
                    } else {
                      return null;
                    }
                  })()}
                </div>
              )}
            </ContainerQuery>
          )}
        </Media>
        <Carousel
          name="bingoMiddle"
          getView={
            <View
              ticketSelect={this.ticketSelect}
              refName={this.props.refName}
              nextAvailableTicketRefs={this.props.nextAvailableTicketRefs}
              nextAvailableTickets={this.props.nextAvailableTickets}
              nextSelectedTicketRefs={this.props.nextSelectedTicketRefs}
              ticketPrice={this.props.ticketPrice}
              priceLabel={this.props.priceLabel}
              nextPurchasedTicketRefs={this.props.nextPurchasedTicketRefs}
              nextPurchasedTickets={this.props.nextPurchasedTickets}
              currentPurchasedTicketRefs={this.props.currentPurchasedTicketRefs}
              currentPurchasedTickets={this.props.currentPurchasedTickets}
            />
          }
          overrideBottom={
            nextPurchasedTicketRefs.length > 0 && {
              'my-tickets': `My Tickets: ${nextPurchasedTicketRefs.length}`
            }
          }
          transformQuery={transformMiddleQuery}
        />
      </div>
    );
  }
}

_BingoMiddle.propTypes = {
  name: PropTypes.string,
  jackpotAmount: PropTypes.number,
  priceLabel: PropTypes.string,
  startDate: PropTypes.number,
  ticketPrice: PropTypes.number,
  type: PropTypes.oneOf(['30', '75', '90']),
  logoImageUrl: PropTypes.string,
  nextAvailableTicketRefs: PropTypes.array,
  nextSelectedTicketRefs: PropTypes.array,
  nextPurchasedTicketRefs: PropTypes.array,
  nextAvailableTickets: PropTypes.object,
  nextPurchasedTickets: PropTypes.object,
  updateStartDate: PropTypes.func,
  selectTicket: PropTypes.func,
  deselectTicket: PropTypes.func,
  refresh: PropTypes.func,
  refName: PropTypes.string.isRequired,
  locked: PropTypes.bool,
  inPlay: PropTypes.bool,
  inProgress: PropTypes.bool
};

_BingoMiddle.defaultProps = {
  name: 'Bingo',
  jackpotAmount: null,
  priceLabel: null,
  refresh: null,
  startDate: 0,
  type: '90',
  ticketPrice: 0,
  locked: false,
  inPlay: false,
  inProgress: false
};

const mapStateToProps = (state, ownProps) => {
  const { refName } = ownProps;
  const roomProps = Bingo.selectors.getDisplayProps(state) || {};
  const startDate = Bingo.selectors.getNextStartDate(state, refName);
  const priceLabel = Bingo.selectors.getPriceLabel(state);
  const inPlay = Bingo.selectors.getInPlay(state, refName);
  const inProgress = Bingo.selectors.getInProgress(state, refName);
  const { name, type, ticketPrice, logoImageUrl } = roomProps;
  return {
    name: name,
    logoImageUrl: logoImageUrl,
    startDate: startDate && new Date(startDate).getTime(),
    type: type,
    ticketPrice: ticketPrice || 0,
    priceLabel: priceLabel,
    locked: Bingo.selectors.getLocked(state, refName, inPlay || inProgress ? 'current' : 'next'),
    nextAvailableTicketRefs: Bingo.selectors.getAvailableTicketRefs(state, refName),
    nextAvailableTickets: Bingo.selectors.getAvailableTickets(state, refName),
    nextPurchasedTickets: Bingo.selectors.getPurchasedTickets(state, refName),
    nextPurchasedTicketRefs: Bingo.selectors.getPurchasedTicketRefs(state, refName),
    nextSelectedTicketRefs: Bingo.selectors.getSelectedTicketRefs(state, refName),
    inPlay: inPlay,
    inProgress: inProgress,
    currentPurchasedTicketRefs: Bingo.selectors.getCurrentPurchasedTicketRefs(
      state,
      refName,
      'current'
    ),
    currentPurchasedTickets: Bingo.selectors.getPurchasedTickets(state, refName, 'current')
  };
};

const DELAY = 1000;

const mapDispatchToProps = (dispatch, ownProps) => ({
  refresh: (timeLeft) => {
    setTimeout(() => Bingo.actions.refresh(dispatch, ownProps.refName), timeLeft + DELAY);
  },
  updateStartDate: () =>
    Api.actions.bingo.startDate({ refName: ownProps.refName, instance: 'next' }, undefined, {
      skipSession: true
    })(dispatch),
  selectTicket: (ticketRefs) => dispatch(Bingo.actions.selectTickets(ownProps.refName, ticketRefs)),
  deselectTicket: (ticketRefs) =>
    dispatch(Bingo.actions.deselectTickets(ownProps.refName, ticketRefs)),
  showInPlay: () => {
    dispatch(CarouselMod.actions.selectTop('bingoMiddle', 'playing'));
    dispatch(CarouselMod.actions.selectBottom('bingoMiddle', 'current-game'));
  },
  showInProgress: () => {
    dispatch(CarouselMod.actions.selectTop('bingoMiddle', 'in-progress'));
    dispatch(CarouselMod.actions.selectBottom('bingoMiddle', 'buy-in-progress'));
  },
  showWaiting: () => {
    dispatch(CarouselMod.actions.selectTop('bingoMiddle', 'waiting'));
    dispatch(CarouselMod.actions.selectBottom('bingoMiddle', 'quick-buy'));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(_BingoMiddle);
